import React from 'react';
import { Modal, Button, Row, Col, Statistic, Table, Alert } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import { Vehicle } from '../../types';

interface FuelEfficiencyModalProps {
  visible: boolean;
  onClose: () => void;
  t: (key: string) => string;
  selectedVehicle: Vehicle | null;
  loading: boolean;
  efficiencySummary: any;
  efficiencyHistory: any[];
  isMobile: boolean;
}

const FuelEfficiencyModal: React.FC<FuelEfficiencyModalProps> = ({
  visible,
  onClose,
  t,
  selectedVehicle,
  loading,
  efficiencySummary,
  efficiencyHistory,
  isMobile
}) => {
  const getChartData = () => {
    if (!efficiencyHistory || efficiencyHistory.length === 0) {
      return [];
    }

    const sortedData = [...efficiencyHistory].sort((a, b) => 
      new Date(a.toDate).getTime() - new Date(b.toDate).getTime()
    );

    return sortedData.map(item => ({
      date: dayjs(item.toDate).format('YYYY-MM-DD'),
      efficiency: parseFloat(item.litersPer100Km?.toFixed(2) || '0'),
      distance: parseFloat(item.distanceKm?.toFixed(2) || '0'),
    }));
  };

  return (
    <Modal
      title={`${t('fuel.efficiency.history')} - ${selectedVehicle?.licensePlate}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('close')}
        </Button>
      ]}
      width={isMobile ? '95%' : 1000}
      style={{ top: isMobile ? 10 : undefined }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      ) : efficiencySummary && efficiencyHistory.length > 0 ? (
        <>
          {/* Summary Statistics */}
          <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('average.efficiency')}
                value={efficiencySummary.averageLitersPer100Km?.toFixed(2)}
                suffix="L/100km"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('best.efficiency')}
                value={efficiencySummary.bestLitersPer100Km?.toFixed(2)}
                suffix="L/100km"
                valueStyle={{ color: '#3f8600', fontSize: isMobile ? 16 : 24 }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('worst.efficiency')}
                value={efficiencySummary.worstLitersPer100Km?.toFixed(2)}
                suffix="L/100km"
                valueStyle={{ color: '#cf1322', fontSize: isMobile ? 16 : 24 }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('total.records')}
                value={efficiencySummary.totalIntervals}
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Col>
          </Row>

          <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
            <Col xs={12}>
              <Statistic
                title={t('total.distance')}
                value={efficiencySummary.totalDistanceKm?.toFixed(0)}
                suffix="km"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Col>
            <Col xs={12}>
              <Statistic
                title={t('total.fuel')}
                value={efficiencySummary.totalFuelLiters?.toFixed(2)}
                suffix="L"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Col>
          </Row>

          {/* Chart */}
          {getChartData().length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h4>{t('efficiency.trend')}</h4>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <LineChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    angle={isMobile ? -45 : 0}
                    textAnchor={isMobile ? 'end' : 'middle'}
                    height={isMobile ? 60 : 30}
                  />
                  <YAxis 
                    label={isMobile ? undefined : { value: t('efficiency.liters.per.100km'), angle: -90, position: 'insideLeft' }}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                  <Line 
                    type="monotone" 
                    dataKey="efficiency" 
                    stroke="#8884d8" 
                    name={t('efficiency.liters.per.100km')}
                    strokeWidth={2}
                    connectNulls={true}
                    dot={{ r: isMobile ? 3 : 4 }}
                    activeDot={{ r: isMobile ? 4 : 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* History Table */}
          <Table
            dataSource={efficiencyHistory}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            size="small"
            scroll={{ x: isMobile ? 600 : undefined }}
            columns={[
              {
                title: t('from.date'),
                dataIndex: 'fromDate',
                key: 'fromDate',
                width: isMobile ? 100 : undefined,
                render: (date) => dayjs(date).format('YYYY-MM-DD')
              },
              {
                title: t('to.date'),
                dataIndex: 'toDate',
                key: 'toDate',
                width: isMobile ? 100 : undefined,
                render: (date) => dayjs(date).format('YYYY-MM-DD')
              },
              {
                title: isMobile ? 'L/100km' : t('efficiency.liters.per.100km'),
                dataIndex: 'litersPer100Km',
                key: 'litersPer100Km',
                width: isMobile ? 80 : undefined,
                render: (value) => value.toFixed(2)
              },
              {
                title: isMobile ? 'km' : t('distance.km'),
                dataIndex: 'distanceKm',
                key: 'distanceKm',
                width: isMobile ? 70 : undefined,
                render: (value) => value.toFixed(0)
              },
              {
                title: isMobile ? 'L' : t('fuel.liters'),
                dataIndex: 'fuelLiters',
                key: 'fuelLiters',
                width: isMobile ? 70 : undefined,
                render: (value) => value.toFixed(2)
              }
            ]}
          />
        </>
      ) : (
        <Alert
          message={t('no.efficiency.data')}
          description={t('no.data.available')}
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export default FuelEfficiencyModal;
